import { Route, Routes } from "react-router-dom"
import SignInView from "../view/SignInView"
import SignUpView from "../view/SignUpView"
import { ReactNode } from "react"
import { useAppSelector } from "../redux/store/hooks"
import { selectProfessionalAuthenticationState } from "../redux/ProfessionalAuthentication"
import DataDashboardView from "../view/dashboard/DataDashboardView"
import ReportDashboardView from "../view/dashboard/ReportDashboardView"
import HomeDashboardView from "../view/HomeDashboardView"
import DeveloperUtilityView from "../view/development/DeveloperUtilityView"
import {
    blurMainView,
    selectOnboardingIsRunningState,
    selectOnboardingStep
} from "../redux/Onboarding"
import PasswordForgottenView from "../view/password_reset/PasswordForgottenView"
import PasswordResetView from "../view/password_reset/PasswordResetView"
import PatientSelectionView from "../view/PatientSelectionView"
import PatientOverview from "../view/PatientOverview"
import PatientDetailView from "../view/PatientDetailView"
import PatientAnalyseView from "../view/PatientAnalyseView"
import HelpView from "../view/HelpView"
import InstructionComponent from "../component/instruction/InstructionComponent"
import PatientHelpTutorialView from "../view/PatientHelpTutorialView"
import LegalView from "../view/LegalView"
import RedirectMedicationView from "../view/RedirectMedicationView"
import RedirectDataView from "../view/RedirectDataView"
import ReportReadView from "../view/ReportReadView"
import MedicationView from "../view/MedicationView"
import { PatientHelpViews, ProfessionalHelpViews } from "../component/FAQContent"
import APIDocumentationView from "../view/APIDocumentationView"
import ProfessionalDocumentationView from "../view/ProfessionalDocumentationView"
import PatientDocumentationView from "../view/PatientDocumentationView"
import ProfessionalHelpTutorialView from "../view/ProfessionalHelpTutorialView"
import ProfessionalHelpTutorialRegistrationView from "../view/ProfessionalHelpTutorialRegistrationView"
import PredictionView from "../view/PredictionView"
import PatientSettingsView from "../view/PatientSettingsView"
import { Navigate } from "react-router-dom"

export enum NAVIGATION_VIEW {
    HOME,
    SIGN_IN,
    SIGN_UP,
    PASSWORD_FORGOTTEN,
    PASSWORD_RESET,

    // Normal App Views
    PATIENT_SELECTION_VIEW,
    PATIENT_OVERVIEW,
    PATIENT_DETAIL_VIEW,
    PATIENT_ANALYSE_VIEW,
    PATIENT_SETTINGS_VIEW,
    LEGAL_VIEW,

    // Redirect Views
    REDIRECT_MEDICATION,
    REDIRECT_DATA,
    READ_REPORT_VIEW,

    // Help Views
    HELP_VIEW,
    PROFESSIONAL_HELP_VIEW,
    PROFESSIONAL_HELP_VIEW_INDEX,
    PROFESSIONAL_HELP_VIEW_INDEX_AND_REDIRECT,
    PROFESSIONAL_HELP_VIEW_TUTORIAL,
    PROFESSIONAL_HELP_VIEW_REGISTRATION,
    PATIENT_HELP_VIEW,
    PATIENT_HELP_VIEW_TUTORIAL,
    // Dashboard Views
    PROFESSIONAL_DASHBOARD,
    PROFESSIONAL_DASHBOARD_MEDICATION,
    PROFESSIONAL_DASHBOARD_REPORT,
    PROFESSIONAL_DASHBOARD_PHQ8,        // kept for redirect: /dashboard/phq8 → /dashboard/prediction
    PROFESSIONAL_DASHBOARD_PREDICTION,  // /dashboard/prediction
    MONITORING_CHAT,                    // /dashboard/monitoring/chat
    PREDICTION_CHAT,                    // /dashboard/prediction/chat

    // Developer Views
    DEVELOPER_UTILITY,
    API_DOCUMENTATION
}

export enum RequiredNavigationAuthentication {
    ALWAYS,
    NONE,
    PROFESSIONAL
    //PATIENT
}

export const defaultPathBuilder = (path: string) => () => path

export type NavigationInfo = {
    path: string
    component: ReactNode
    authentication: RequiredNavigationAuthentication
    pathBuilder: (params: string[]) => string
}

export const NavigationPaths: Record<NAVIGATION_VIEW, NavigationInfo> = {
    [NAVIGATION_VIEW.HOME]: {
        path: "/",
        component: <HomeDashboardView />,
        authentication: RequiredNavigationAuthentication.NONE,
        pathBuilder: defaultPathBuilder("/")
    },
    [NAVIGATION_VIEW.DEVELOPER_UTILITY]: {
        path: "/dev",
        component: <DeveloperUtilityView />,
        authentication: RequiredNavigationAuthentication.ALWAYS,
        pathBuilder: defaultPathBuilder("/dev")
    },
    [NAVIGATION_VIEW.SIGN_IN]: {
        path: "/login",
        component: <SignInView />,
        authentication: RequiredNavigationAuthentication.NONE,
        pathBuilder: defaultPathBuilder("/login")
    },
    [NAVIGATION_VIEW.SIGN_UP]: {
        path: "/register",
        component: <SignUpView />,
        authentication: RequiredNavigationAuthentication.NONE,
        pathBuilder: defaultPathBuilder("/register")
    },
    [NAVIGATION_VIEW.PASSWORD_FORGOTTEN]: {
        path: "/password/forgotten",
        component: <PasswordForgottenView />,
        authentication: RequiredNavigationAuthentication.NONE,
        pathBuilder: defaultPathBuilder("/")
    },
    [NAVIGATION_VIEW.PASSWORD_RESET]: {
        path: "/user/password/:mode/:token",
        component: <PasswordResetView />,
        authentication: RequiredNavigationAuthentication.NONE,
        pathBuilder: (params: string[]) =>
            `/user/password/${params[0]}/${params[1]}`
    },
    [NAVIGATION_VIEW.PROFESSIONAL_DASHBOARD]: {
        path: "/dashboard/data",
        component: <DataDashboardView />,
        authentication: RequiredNavigationAuthentication.PROFESSIONAL,
        pathBuilder: defaultPathBuilder("/dashboard/data")
    },
    [NAVIGATION_VIEW.PROFESSIONAL_DASHBOARD_MEDICATION]: {
        path: "/dashboard/medication",
        component: <MedicationView />,
        authentication: RequiredNavigationAuthentication.PROFESSIONAL,
        pathBuilder: defaultPathBuilder("/dashboard/medication")
    },
    [NAVIGATION_VIEW.PROFESSIONAL_DASHBOARD_REPORT]: {
        path: "/dashboard/report",
        component: <ReportDashboardView />,
        authentication: RequiredNavigationAuthentication.PROFESSIONAL,
        pathBuilder: defaultPathBuilder("/dashboard/report")
    },
    [NAVIGATION_VIEW.PROFESSIONAL_DASHBOARD_PHQ8]: {
        path: "/dashboard/phq8",
        component: <Navigate to="/dashboard/prediction" replace />,
        authentication: RequiredNavigationAuthentication.PROFESSIONAL,
        pathBuilder: defaultPathBuilder("/dashboard/prediction")
    },
    [NAVIGATION_VIEW.PROFESSIONAL_DASHBOARD_PREDICTION]: {
        path: "/dashboard/prediction",
        component: <PredictionView />,
        authentication: RequiredNavigationAuthentication.PROFESSIONAL,
        pathBuilder: defaultPathBuilder("/dashboard/prediction")
    },
    [NAVIGATION_VIEW.MONITORING_CHAT]: {
        path: "/dashboard/monitoring/chat",
        component: <PatientOverview />,
        authentication: RequiredNavigationAuthentication.PROFESSIONAL,
        pathBuilder: defaultPathBuilder("/patient/overview")
    },
    [NAVIGATION_VIEW.PREDICTION_CHAT]: {
        path: "/dashboard/prediction/chat",
        component: <PredictionView />,
        authentication: RequiredNavigationAuthentication.PROFESSIONAL,
        pathBuilder: defaultPathBuilder("/dashboard/prediction")
    },
    [NAVIGATION_VIEW.PATIENT_SELECTION_VIEW]: {
        path: "/",
        component: <PatientSelectionView />,
        authentication: RequiredNavigationAuthentication.PROFESSIONAL,
        pathBuilder: defaultPathBuilder("/")
    },
    [NAVIGATION_VIEW.PATIENT_OVERVIEW]: {
        path: "/patient/overview",
        component: <PatientOverview />,
        authentication: RequiredNavigationAuthentication.PROFESSIONAL,
        pathBuilder: defaultPathBuilder("/patient/overview")
    },
    [NAVIGATION_VIEW.PATIENT_DETAIL_VIEW]: {
        path: "/patient/detail",
        component: <PatientDetailView />,
        authentication: RequiredNavigationAuthentication.PROFESSIONAL,
        pathBuilder: defaultPathBuilder("/patient/detail")
    },
    [NAVIGATION_VIEW.PATIENT_ANALYSE_VIEW]: {
        path: "/patient/analyse",
        component: <PatientAnalyseView />,
        authentication: RequiredNavigationAuthentication.PROFESSIONAL,
        pathBuilder: defaultPathBuilder("/patient/analyse")
    },
    [NAVIGATION_VIEW.PATIENT_SETTINGS_VIEW]: {
        path: "/patient/settings",
        component: <PatientSettingsView />,
        authentication: RequiredNavigationAuthentication.PROFESSIONAL,
        pathBuilder: defaultPathBuilder("/patient/settings")
    },
    [NAVIGATION_VIEW.HELP_VIEW]: {
        path: "/help",
        component: <HelpView />,
        authentication: RequiredNavigationAuthentication.ALWAYS,
        pathBuilder: defaultPathBuilder("/help")
    },
    [NAVIGATION_VIEW.API_DOCUMENTATION]: {
        path: "/api",
        component: <APIDocumentationView />,
        authentication: RequiredNavigationAuthentication.ALWAYS,
        pathBuilder: defaultPathBuilder("/api")
    },
    [NAVIGATION_VIEW.PROFESSIONAL_HELP_VIEW]: {
        path: "/help/professional",
        component: <ProfessionalDocumentationView />,
        authentication: RequiredNavigationAuthentication.PROFESSIONAL,
        pathBuilder: () => `/help/professional`
    },
    [NAVIGATION_VIEW.PROFESSIONAL_HELP_VIEW_INDEX]: {
        path: "/help/professional/:index",
        component: <ProfessionalDocumentationView />,
        authentication: RequiredNavigationAuthentication.PROFESSIONAL,
        pathBuilder: (params) => `/help/professional/${params[0]}`
    },
    [NAVIGATION_VIEW.PROFESSIONAL_HELP_VIEW_INDEX_AND_REDIRECT]: {
        path: "/help/professional/:index/:location",
        component: <ProfessionalDocumentationView />,
        authentication: RequiredNavigationAuthentication.PROFESSIONAL,
        pathBuilder: (params) =>
            `/help/professional/${params[0]}/${encodeURIComponent(params[1])}`
    },
    [NAVIGATION_VIEW.PROFESSIONAL_HELP_VIEW_TUTORIAL]: {
        path: "/help/professional/tutorial1",
        component: <ProfessionalHelpTutorialView />,
        authentication: RequiredNavigationAuthentication.ALWAYS,
        pathBuilder: defaultPathBuilder("/help/professional/tutorial1")
    },
    [NAVIGATION_VIEW.PROFESSIONAL_HELP_VIEW_REGISTRATION]: {
        path: "/help/professional/tutorial2",
        component: <ProfessionalHelpTutorialRegistrationView />,
        authentication: RequiredNavigationAuthentication.ALWAYS,
        pathBuilder: defaultPathBuilder("/help/professional/tutorial2")
    },
    [NAVIGATION_VIEW.PATIENT_HELP_VIEW]: {
        path: "/help/patient",
        component: <PatientDocumentationView />,
        authentication: RequiredNavigationAuthentication.ALWAYS,
        pathBuilder: defaultPathBuilder("/help/patient")
    },
    [NAVIGATION_VIEW.PATIENT_HELP_VIEW_TUTORIAL]: {
        path: "/help/patient/tutorial",
        component: <PatientHelpTutorialView />,
        authentication: RequiredNavigationAuthentication.ALWAYS,
        pathBuilder: defaultPathBuilder("/help/patient/tutorial")
    },
    [NAVIGATION_VIEW.LEGAL_VIEW]: {
        path: "/legal",
        component: <LegalView />,
        authentication: RequiredNavigationAuthentication.ALWAYS,
        pathBuilder: defaultPathBuilder("/legal")
    },
    [NAVIGATION_VIEW.REDIRECT_MEDICATION]: {
        path: "/redirect/medication/:patient",
        component: <RedirectMedicationView />,
        authentication: RequiredNavigationAuthentication.PROFESSIONAL,
        pathBuilder: (params) => `/redirect/medication/${params[0]}`
    },
    [NAVIGATION_VIEW.REDIRECT_DATA]: {
        path: "/redirect/data/:patient/:valueType",
        component: <RedirectDataView />,
        authentication: RequiredNavigationAuthentication.PROFESSIONAL,
        pathBuilder: (params) => `/redirect/data/${params[0]}/${params[1]}`
    },
    [NAVIGATION_VIEW.READ_REPORT_VIEW]: {
        path: "/report/read/:report",
        component: <ReportReadView />,
        authentication: RequiredNavigationAuthentication.PROFESSIONAL,
        pathBuilder: (params) => `/report/read/${params[0]}`
    }
}

function NavigationController() {
    const professionalAuthenticationState = useAppSelector(
        selectProfessionalAuthenticationState
    )

    function getRoutes(auth: RequiredNavigationAuthentication): ReactNode {
        return Object.values(NavigationPaths)
            .filter((navigation) => navigation.authentication === auth)
            .map((navigation) => (
                <Route
                    key={navigation.path}
                    path={navigation.path}
                    element={navigation.component}
                />
            ))
    }

    const onboardingIsRunning = useAppSelector(selectOnboardingIsRunningState)
    const onboardingStep = useAppSelector(selectOnboardingStep)

    return (
        <div
            className={
                "mt-[0px] " +
                (blurMainView(onboardingIsRunning, onboardingStep)
                    ? "blur"
                    : "")
            }>
            <Routes>
                {getRoutes(RequiredNavigationAuthentication.ALWAYS)}
                {!professionalAuthenticationState &&
                    getRoutes(RequiredNavigationAuthentication.NONE)}
                {professionalAuthenticationState &&
                    getRoutes(RequiredNavigationAuthentication.PROFESSIONAL)}
                {PatientHelpViews.map((h) => (
                    <Route
                        key={h.path}
                        path={h.path}
                        element={<InstructionComponent {...h} />}
                    />
                ))}
                {professionalAuthenticationState && (
                    <>
                        {ProfessionalHelpViews.map((h) => (
                            <Route
                                key={h.path}
                                path={h.path}
                                element={<InstructionComponent {...h} />}
                            />
                        ))}
                    </>
                )}
            </Routes>
        </div>
    )
}

export default NavigationController
